import axios, { AxiosRequestConfig, AxiosResponse } from "axios";
import store from "@/store/index";
import configuration from "./config";
import { userIsAuthorize } from "./JWTservice";
import router from "@/router";
import routing from "@/constants/routes";

export class ApiResponse<T = any> {
  constructor(public data: T) {}
}

type Payload = object | number | string | null;

export interface EndpointCallParams<P extends Payload> {
  payload: P;
}

export interface ApiCallParams<P extends Payload = any>
  extends EndpointCallParams<P> {
  isAbsoluteUrl?: boolean; // TODO: Find a better way to handle absolute urls
}

async function parseResponse<T = any>(
  response: Response
): Promise<T | null | boolean> {
  switch (response.status) {
    case 200:
    case 201: {
      return response.json();
    }

    case 202:
    case 204: {
      return true;
    }

    case 400: {
      store.commit("updateIsGenericErrorModaleActiveMutation", true);
      break;
    }

    case 401: {
      router.push({ path: routing.LOGIN });
      break;
    }

    case 404: {
      break;
    }

    case 500: {
      store.commit("updateIsGenericErrorModaleActiveMutation", true);
      break;
    }

    default: {
      store.commit("updateIsGenericErrorModaleActiveMutation", true);
      return false;
    }
  }
  return false;
}

export default async function requestToApi<T = any>(
  path = "",
  httpMethod: "post" | "put" | "patch" | "get" | "delete" = "get",
  needAuth = true,
  body = {},
  isFile = false
): Promise<T | null> {
  const contentType = isFile ? "multipart/form-data" : "application/json";

  const myHeaders = new Headers({ "Content-Type": contentType });

  const { apiTokenStorageKey, apiUrl } = configuration;

  const token = sessionStorage.getItem(apiTokenStorageKey);
  const url = `${apiUrl}${path}`;

  let requestResult = null;

  if (needAuth) {
    if (await userIsAuthorize()) {
      if (token && token !== "") {
        myHeaders.append("Authorization", `Bearer ${token}`);
      } else {
        return null;
      }
    } else {
      return null;
    }
  }

  let myInit: RequestInit = {
    method: httpMethod,
    headers: myHeaders,
    cache: "default"
  };
  const props = isFile ? { data: body } : { body: JSON.stringify(body) };

  if (httpMethod.toUpperCase() !== "GET") {
    myInit = { ...myInit, ...props };
  }

  let responseStatus = 0;

  await fetch(url, myInit)
    .then(async response => {
      const { status } = response;
      responseStatus = status;
      requestResult = await parseResponse(response);
    })
    .catch(() => {
      if (responseStatus === 200) {
        requestResult = true;
      } else {
        store.commit("updateIsGenericErrorModaleActiveMutation", true);
        requestResult = false;
      }
    });

  return requestResult;
}

export async function apiGet<T = any>(url: string): Promise<ApiResponse<T>> {
  const result = await requestToApi(url, "get", true);
  return new ApiResponse(result);
}
export async function apiPost<T = any>(
  url: string,
  params?: ApiCallParams
): Promise<ApiResponse<T>> {
  const result = await requestToApi(url, "post", true, params?.payload);
  return new ApiResponse(result);
}
export async function apiPostFile<T = any>(
  url: string,
  params: ApiCallParams
): Promise<ApiResponse<T>> {
  const { apiTokenStorageKey, apiUrl } = configuration;

  const formData = new FormData();
  formData.set("File", params.payload);

  const token = sessionStorage.getItem(apiTokenStorageKey);
  const uploadURL = `${apiUrl}${url}`;

  const config = {
    headers: {
      "content-type": "multipart/form-data",
      Authorization: `Bearer ${token}`
    }
  };

  let result: any = null;

  await axios
    .post(uploadURL, formData, config)
    .catch(() => {
      store.commit("updateIsGenericErrorModaleActiveMutation", true);
    })
    .then((response: any) => {
      result = response?.data;
    });

  return new ApiResponse(result);
}
export async function apiPut<T = any>(
  url: string,
  params?: ApiCallParams
): Promise<ApiResponse<T>> {
  const result = await requestToApi(url, "put", true, params?.payload);
  return new ApiResponse(result);
}

export async function apiDelete(
  url: string,
  params?: ApiCallParams
): Promise<ApiResponse<any>> {
  const result = await requestToApi(url, "delete", true, params?.payload);
  return new ApiResponse(result);
}
export async function apiPatch(
  url: string,
  params?: ApiCallParams
): Promise<ApiResponse<any>> {
  const result = await requestToApi(url, "patch", true, params?.payload);
  return new ApiResponse(result);
}

function parseDetailedResponse<T = any>(
  response: AxiosResponse
): T | null | boolean {
  switch (response.status) {
    case 200:
    case 201: {
      return response.data;
    }

    case 202:
    case 204: {
      return true;
    }

    case 400: {
      const errorMessage = response.data.Error;
      store.commit("updateGenericErrorModalMessageMutation", errorMessage);
      store.commit("updateIsGenericErrorModaleActiveMutation", true);
      break;
    }

    case 401: {
      router.push({ path: routing.LOGIN });
      break;
    }

    case 404: {
      break;
    }

    case 500: {
      const errorMessage = response.data.Error;
      store.commit("updateGenericErrorModalMessageMutation", errorMessage);
      store.commit("updateIsGenericErrorModaleActiveMutation", true);
      break;
    }

    default: {
      store.commit("updateIsGenericErrorModaleActiveMutation", true);
      return false;
    }
  }
  return false;
}

export async function apiPostWithDetailErrorResponse<T = any>(
  path: string,
  params?: ApiCallParams
): Promise<T | null> {
  const { apiTokenStorageKey, apiUrl } = configuration;

  const token = sessionStorage.getItem(apiTokenStorageKey);
  const url = `${apiUrl}${path}`;
  let requestResult: any = null;

  const config: AxiosRequestConfig = {
    headers: {
      "Content-Type": "application/json"
    }
  };

  const isUserAuthorized = await userIsAuthorize();

  if (isUserAuthorized && token !== "") {
    config.headers.authorization = `Bearer ${token}`;
  }

  const data = JSON.stringify(params?.payload);

  await axios
    .post(url, data, config)
    .then(function(response) {
      requestResult = parseDetailedResponse(response);
    })
    .catch(function(error) {
      requestResult = parseDetailedResponse(error.response);
    });

  return requestResult;
}

export async function apiPutWithDetailErrorResponse<T = any>(
  path: string,
  params?: ApiCallParams
): Promise<T | null> {
  const { apiTokenStorageKey, apiUrl } = configuration;

  const token = sessionStorage.getItem(apiTokenStorageKey);
  const url = `${apiUrl}${path}`;
  let requestResult: any = null;

  const config: AxiosRequestConfig = {
    headers: {
      "Content-Type": "application/json"
    }
  };

  const isUserAuthorized = await userIsAuthorize();

  if (isUserAuthorized && token !== "") {
    config.headers.authorization = `Bearer ${token}`;
  }

  const data = JSON.stringify(params?.payload);

  await axios
    .put(url, data, config)
    .then(function(response) {
      requestResult = parseDetailedResponse(response);
    })
    .catch(function(error) {
      requestResult = parseDetailedResponse(error.response);
    });

  return requestResult;
}

export const api = {
  apiGet,
  apiPost,
  apiPostFile,
  apiPut,
  apiPatch,
  apiDelete
};
